//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Map layers data form
//
//  Copyright:    © 2025 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.7.0
//    001   03.10.25 Sean Flook       IMANN-1812 Initial version.
//    002   30.10.25 Sean Flook       IMANN-1812 Use the new fields to get the correct data.
//    003   06.11.25 Sean Flook       IMANN-2092 Bug fix.
//    004   25.02.26 Sean Flook       IMANN-2324 When editing a layer ensure the full list of layers are used.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useContext, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

import SettingsContext from "../context/settingsContext";
import UserContext from "../context/userContext";

import { GetMapLayersUrl } from "../configuration/ADSConfig";

import { Box } from "@mui/material";
import MapLayerGridTab from "../tabs/MapLayerGridTab";

import EditMapLayersDialog from "../dialogs/EditMapLayersDialog";
import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`setting-tabpanel-${index}`}
      aria-labelledby={`setting-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

MapLayersDataForm.propTypes = {
  nodeId: PropTypes.oneOf(["BASE_MAPS", "REFERENCE_MAPS", "CUSTOM_MAPS"]).isRequired,
};

function MapLayersDataForm({ nodeId }) {
  const settingsContext = useContext(SettingsContext);
  const userContext = useContext(UserContext);

  const [mapLayerType, setMapLayerType] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [baseMapData, setBaseMapData] = useState([]);
  const [referenceMapData, setReferenceMapData] = useState([]);
  const [customMapData, setCustomMapData] = useState([]);

  const [editData, setEditData] = useState({});
  const [showEditDialog, setShowEditDialog] = useState(false);

  const deletePkId = useRef(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const [mapLayerErrors, setMapLayerErrors] = useState(null);

  const newLayer = useRef(false);

  const currentLayerData = () => {
    let data = [];
    switch (nodeId) {
      case "BASE_MAPS":
        data = baseMapData;
        break;
      case "REFERENCE_MAPS":
        data = referenceMapData;
        break;
      case "CUSTOM_MAPS":
        data = customMapData;
        break;
      default:
        break;
    }
    return data;
  };

  const handleAddMapLayer = () => {
    newLayer.current = true;

    const data = currentLayerData();

    const newRecord = {
      pkId: data && data.length > 0 ? data.length + 1 : 1,
      layerId: "",
      layerType: "",
      layerPosition: data && data.length > 0 ? data.length + 1 : 1,
      url: "",
      title: "",
      copyright: "",
      displayInList: true,
      visible: true,
      minScale: 0,
      maxScale: 0,
      opacity: 1.0,
      esuSnap: false,
      blpuSnap: false,
      extentSnap: false,
      globalLayer: true,
      serviceProvider: "",
      geometryType: "",
      layerKey: "",
      layerUsername: "",
      layerPassword: "",
      activeLayerId: "",
      serviceMode: "",
      propertyName: "",
      usePaging: false,
      maxBatchSize: 100,
      thumbnailUrl: "",
      mapLayerType: mapLayerType === "base" ? 1 : mapLayerType === "reference" ? 2 : 3,
    };

    setMapLayerErrors(null);
    setEditData(newRecord);
    setShowEditDialog(true);
  };

  const handleEditMapLayer = (id) => {
    const data = currentLayerData();
    const editRecord = data.find((x) => x.pkId === id);
    if (editRecord) {
      setMapLayerErrors(null);
      setEditData(editRecord);
      setShowEditDialog(true);
    }
  };

  const handleDeleteMapLayer = (id) => {
    deletePkId.current = id;
    setOpenDeleteConfirmation(true);
  };

  /**
   * Event to handle when the data has been changed.
   *
   * @param {object} updatedData The data that has been changed.
   */
  const handleDataChanged = (updatedData) => {
    setEditData(updatedData);
  };

  /**
   * Event to handle when the list of errors has changed.
   *
   * @param {Array} mapLayerErrors The list of errors.
   */
  const handleErrorsChanged = (mapLayerErrors) => {
    setMapLayerErrors(mapLayerErrors);
  };

  /**
   * Event to handle when the edit dialog is closed.
   */
  const handleCloseEditDialog = () => {
    newLayer.current = false;
    setShowEditDialog(false);
  };

  /**
   * Method to get the data objects required to save the data.
   *
   * @param {object} sourceData The source data to save.
   * @returns {object} The object that can be passed to the API to be saved.
   */
  const getMapLayerSaveData = (sourceData) => {
    if (newLayer.current)
      return {
        layerId: sourceData.layerId,
        layerType: sourceData.layerType,
        layerPosition: sourceData.layerPosition,
        url: sourceData.url,
        title: sourceData.title,
        copyright: sourceData.copyright,
        displayInList: sourceData.displayInList,
        visible: sourceData.visible,
        minScale: sourceData.minScale,
        maxScale: sourceData.maxScale,
        opacity: sourceData.opacity,
        esuSnap: sourceData.esuSnap,
        blpuSnap: sourceData.blpuSnap,
        extentSnap: sourceData.extentSnap,
        globalLayer: sourceData.globalLayer,
        serviceProvider: sourceData.serviceProvider,
        geometryType: sourceData.geometryType,
        layerKey: sourceData.layerKey,
        layerUsername: sourceData.layerUsername,
        layerPassword: sourceData.layerPassword,
        activeLayerId: sourceData.activeLayerId,
        serviceMode: sourceData.serviceMode,
        propertyName: sourceData.propertyName,
        usePaging: sourceData.usePaging,
        maxBatchSize: sourceData.maxBatchSize,
        thumbnailUrl: sourceData.thumbnailUrl,
        mapLayerType: mapLayerType === "base" ? 1 : mapLayerType === "reference" ? 2 : 3,
      };
    else
      return {
        layerId: sourceData.layerId,
        layerType: sourceData.layerType,
        layerPosition: sourceData.layerPosition,
        url: sourceData.url,
        title: sourceData.title,
        copyright: sourceData.copyright,
        displayInList: sourceData.displayInList,
        visible: sourceData.visible,
        minScale: sourceData.minScale,
        maxScale: sourceData.maxScale,
        opacity: sourceData.opacity,
        esuSnap: sourceData.esuSnap,
        blpuSnap: sourceData.blpuSnap,
        extentSnap: sourceData.extentSnap,
        globalLayer: sourceData.globalLayer,
        serviceProvider: sourceData.serviceProvider,
        geometryType: sourceData.geometryType,
        layerKey: sourceData.layerKey,
        layerUsername: sourceData.layerUsername,
        layerPassword: sourceData.layerPassword,
        activeLayerId: sourceData.activeLayerId,
        serviceMode: sourceData.serviceMode,
        propertyName: sourceData.propertyName,
        usePaging: sourceData.usePaging,
        maxBatchSize: sourceData.maxBatchSize,
        thumbnailUrl: sourceData.thumbnailUrl,
        mapLayerType: mapLayerType === "base" ? 1 : mapLayerType === "reference" ? 2 : 3,
        pkId: sourceData.pkId,
      };
  };

  /**
   * Event to handle when a layer has been edited.
   *
   * @param {object} updatedData The data that has been changed.
   */
  const handleDoneEditLayer = async (updatedData) => {
    const mapLayerApiUrl = await GetMapLayersUrl(newLayer.current ? "POST" : "PUT", userContext.currentUser);
    const saveData = getMapLayerSaveData(updatedData);
    const isNewLayer = newLayer.current;

    if (mapLayerApiUrl) {
      if (userContext.currentUser.showMessages) console.log("[DEBUG] saveData", JSON.stringify(saveData));

      fetch(mapLayerApiUrl.url, {
        headers: mapLayerApiUrl.headers,
        crossDomain: true,
        method: mapLayerApiUrl.type,
        body: JSON.stringify(updatedData),
      })
        .then((res) => (res.ok ? res : Promise.reject(res)))
        .then((res) => res.json())
        .then((result) => {
          let editedData = [];
          if (isNewLayer) {
            if (settingsContext.mapLayers && settingsContext.mapLayers.length > 0) {
              editedData = settingsContext.mapLayers.map((x) => x);
              editedData.push(result);
            } else editedData = [result];
          } else {
            if (settingsContext.mapLayers && settingsContext.mapLayers.length > 0) {
              editedData = settingsContext.mapLayers.map((x) => [result].find((rec) => rec.pkId === x.pkId) || x);
            } else editedData = [result];
          }
          settingsContext.onMapLayersChange(editedData);
          setShowEditDialog(false);
        })
        .catch((res) => {
          switch (res.status) {
            case 400:
              res.json().then((body) => {
                if (userContext.currentUser.showMessages)
                  console.error(
                    `[400 ERROR] ${isNewLayer ? "Creating" : "Updating"} ${
                      mapLayerType === 1 ? "base" : mapLayerType === 2 ? "reference" : "custom"
                    } map layer`,
                    body.errors
                  );

                let errorMapLayer = [];

                for (const [key, value] of Object.entries(body.errors)) {
                  if (key.toLowerCase().includes("title")) {
                    errorMapLayer.push({ field: "Title", errors: value });
                  } else if (key.toLowerCase().includes("layertype")) {
                    errorMapLayer.push({ field: "LayerType", errors: value });
                  } else if (key.toLowerCase().includes("copyright")) {
                    errorMapLayer.push({ field: "Copyright", errors: value });
                  } else if (key.toLowerCase().includes("serviceprovider")) {
                    errorMapLayer.push({ field: "ServiceProvider", errors: value });
                  } else if (key.toLowerCase().includes("layerkey")) {
                    errorMapLayer.push({ field: "LayerKey", errors: value });
                  } else if (key.toLowerCase().includes("layerusername")) {
                    errorMapLayer.push({ field: "LayerUsername", errors: value });
                  } else if (key.toLowerCase().includes("layerpassword")) {
                    errorMapLayer.push({ field: "LayerPassword", errors: value });
                  } else if (key.toLowerCase().includes("activelayerid")) {
                    errorMapLayer.push({ field: "ActiveLayerId", errors: value });
                  } else if (key.toLowerCase().includes("servicemode")) {
                    errorMapLayer.push({ field: "ServiceMode", errors: value });
                  } else if (key.toLowerCase().includes("propertyname")) {
                    errorMapLayer.push({ field: "PropertyName", errors: value });
                  } else if (key.toLowerCase().includes("layerid")) {
                    errorMapLayer.push({ field: "LayerId", errors: value });
                  } else if (key.toLowerCase().includes("url")) {
                    errorMapLayer.push({ field: "Url", errors: value });
                  } else if (key.toLowerCase().includes("opacity")) {
                    errorMapLayer.push({ field: "Opacity", errors: value });
                  } else if (key.toLowerCase().includes("thumbnailurl")) {
                    errorMapLayer.push({ field: "ThumbnailUrl", errors: value });
                  } else {
                    errorMapLayer.push({ field: key, errors: value });
                  }
                }

                if (errorMapLayer.length > 0) {
                  setMapLayerErrors(errorMapLayer);
                }
              });
              break;

            case 401:
              useContext.onExpired();
              setMapLayerErrors([
                {
                  field: "Title",
                  errors: [`You are not authorised to ${isNewLayer ? "create" : "update"} this map layer.`],
                },
              ]);
              res.json().then((body) => {
                if (userContext.currentUser.showMessages)
                  console.error(
                    `[401 ERROR] ${isNewLayer ? "Creating" : "Updating"} ${
                      mapLayerType === 1 ? "base" : mapLayerType === 2 ? "reference" : "custom"
                    } map layer`,
                    body
                  );
              });
              break;

            default:
              if (userContext.currentUser.showMessages)
                console.error(
                  `[${res.status} ERROR] HandleDoneEditLayer - ${isNewLayer ? "Creating" : "Updating"} ${
                    mapLayerType === 1 ? "base" : mapLayerType === 2 ? "reference" : "custom"
                  } map layer.`,
                  res
                );

              res.text().then((response) => {
                if (userContext.currentUser.showMessages)
                  console.error(
                    `[${res.status} ERROR] HandleDoneEditLayer - ${isNewLayer ? "Creating" : "Updating"} ${
                      mapLayerType === 1 ? "base" : mapLayerType === 2 ? "reference" : "custom"
                    } map layer.`,
                    response,
                    res
                  );

                const responseData = response.replace("[{", "").replace("}]", "").split(',"');

                let errorTitle = "";
                let errorDescription = "";

                for (const errorData of responseData) {
                  if (errorData.includes("errorTitle")) errorTitle = errorData.substr(13).replace('"', "");
                  else if (errorData.includes("errorDescription"))
                    errorDescription = errorData.substr(19).replace('"', "");

                  if (errorTitle && errorTitle.length > 0 && errorDescription && errorDescription.length > 0) break;
                }

                setMapLayerErrors([
                  {
                    field: "Title",
                    errors: [`${errorTitle}: ${errorDescription}`],
                  },
                ]);
              });
              break;
          }
        });
    }

    newLayer.current = false;
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the deletion; otherwise false.
   */
  const handleCloseDeleteConfirmation = async (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);

    if (deleteConfirmed && deletePkId.current) {
      const mapLayerApiUrl = await GetMapLayersUrl("DELETE", userContext.currentUser);
      const data = currentLayerData();

      if (mapLayerApiUrl) {
        return await fetch(`${mapLayerApiUrl.url}/${deletePkId.current}`, {
          headers: mapLayerApiUrl.headers,
          crossDomain: true,
          method: mapLayerApiUrl.type,
        })
          .then((res) => (res.ok ? res : Promise.reject(res)))
          .then((res) => res.json())
          .then((result) => {
            settingsContext.onMapLayersChange(data.filter((x) => x.pkId !== deletePkId.current));
          })
          .catch((res) => {
            switch (res.status) {
              case 400:
                res.json().then((body) => {
                  if (userContext.currentUser.showMessages)
                    console.error(`[400 ERROR] Deleting map layer`, body.errors);
                });
                break;

              case 401:
                userContext.onExpired();
                break;

              default:
                res.text().then((response) => {
                  if (userContext.currentUser.showMessages)
                    console.error(`[${res.status} ERROR] doDeleteMapLayer - Deleting map layer.`, response, res);

                  const responseData = response.replace("[{", "").replace("}]", "").split(',"');

                  let errorTitle = "";
                  let errorDescription = "";

                  for (const errorData of responseData) {
                    if (errorData.includes("errorTitle")) errorTitle = errorData.substr(13).replace('"', "");
                    else if (errorData.includes("errorDescription"))
                      errorDescription = errorData.substr(19).replace('"', "");

                    if (errorTitle && errorTitle.length > 0 && errorDescription && errorDescription.length > 0) break;
                  }
                });
                break;
            }
          });
      }
    }
  };

  useEffect(() => {
    switch (nodeId) {
      case "BASE_MAPS":
        if (settingsContext.mapLayers && settingsContext.mapLayers.length > 0) {
          setBaseMapData(settingsContext.mapLayers.filter((layer) => layer.mapLayerType === 1));
        } else {
          setBaseMapData([]);
        }
        setMapLayerType("base");
        setCurrentTab(1);
        break;
      case "REFERENCE_MAPS":
        if (settingsContext.mapLayers && settingsContext.mapLayers.length > 0) {
          setReferenceMapData(settingsContext.mapLayers.filter((layer) => layer.mapLayerType === 2));
        } else {
          setReferenceMapData([]);
        }
        setMapLayerType("reference");
        setCurrentTab(2);
        break;
      case "CUSTOM_MAPS":
        if (settingsContext.mapLayers && settingsContext.mapLayers.length > 0) {
          setCustomMapData(settingsContext.mapLayers.filter((layer) => layer.mapLayerType === 3));
        } else {
          setCustomMapData([]);
        }
        setMapLayerType("custom");
        setCurrentTab(3);
        break;
      default:
        setBaseMapData([]);
        setReferenceMapData([]);
        setCustomMapData([]);
        setMapLayerType("");
        setCurrentTab(0);
    }
  }, [nodeId, settingsContext.mapLayers, settingsContext.mapSymbology]);

  return (
    <div id="map-layers-data-form">
      <TabPanel value={currentTab} index={1}>
        <MapLayerGridTab
          variant={mapLayerType}
          data={baseMapData}
          onAddMap={handleAddMapLayer}
          onEditMap={handleEditMapLayer}
          onDeleteMap={handleDeleteMapLayer}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={2}>
        <MapLayerGridTab
          variant={mapLayerType}
          data={referenceMapData}
          onAddMap={handleAddMapLayer}
          onEditMap={handleEditMapLayer}
          onDeleteMap={handleDeleteMapLayer}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={3}>
        <MapLayerGridTab
          variant={mapLayerType}
          data={customMapData}
          onAddMap={handleAddMapLayer}
          onEditMap={handleEditMapLayer}
          onDeleteMap={handleDeleteMapLayer}
        />
      </TabPanel>
      <EditMapLayersDialog
        isOpen={showEditDialog}
        variant={mapLayerType}
        isNew={newLayer.current}
        data={editData}
        errors={mapLayerErrors}
        onDataChanged={handleDataChanged}
        onErrorsChanged={handleErrorsChanged}
        onDone={(updatedData) => handleDoneEditLayer(updatedData)}
        onClose={handleCloseEditDialog}
      />
      <ConfirmDeleteDialog variant="map layer" open={openDeleteConfirmation} onClose={handleCloseDeleteConfirmation} />
    </div>
  );
}

export default MapLayersDataForm;
